import React from 'react';
import { Skeleton, keyframes } from '@mui/material';

// 더 생동감 있는 wave 애니메이션
const enhancedWave = keyframes`
  0% {
    transform: translateX(-100%);
    opacity: 0.1;
  }
  50% {
    transform: translateX(-50%);
    opacity: 0.8;
  }
  100% {
    transform: translateX(100%);
    opacity: 0.1;
  }
`;

// 강화된 펄스 애니메이션 (더 역동적이고 생동감 있는 효과)
const enhancedPulse = keyframes`
  0% {
    opacity: 0.3;
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4);
  }
  25% {
    opacity: 0.6;
    transform: scale(1.01);
    box-shadow: 0 0 0 4px rgba(25, 118, 210, 0.2);
  }
  50% {
    opacity: 0.9;
    transform: scale(1.03);
    box-shadow: 0 0 0 8px rgba(25, 118, 210, 0.1);
  }
  75% {
    opacity: 0.6;
    transform: scale(1.01);
    box-shadow: 0 0 0 4px rgba(25, 118, 210, 0.2);
  }
  100% {
    opacity: 0.3;
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4);
  }
`;

// 그라데이션 흐르는 애니메이션
const gradientFlow = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

// 반짝이는 효과
const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

// 기존 펄스 애니메이션 (심장박동 같은 효과)
const pulse = keyframes`
  0% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.02);
  }
  100% {
    opacity: 0.4;
    transform: scale(1);
  }
`;

// 강력한 펄스 + 글로우 효과
const powerPulse = keyframes`
  0% {
    opacity: 0.2;
    transform: scale(0.98);
    filter: brightness(1) blur(0px);
    background: linear-gradient(45deg, rgba(25, 118, 210, 0.1), rgba(156, 39, 176, 0.1));
  }
  33% {
    opacity: 0.7;
    transform: scale(1.02);
    filter: brightness(1.1) blur(0.5px);
    background: linear-gradient(45deg, rgba(25, 118, 210, 0.2), rgba(156, 39, 176, 0.2));
  }
  66% {
    opacity: 0.9;
    transform: scale(1.04);
    filter: brightness(1.2) blur(1px);
    background: linear-gradient(45deg, rgba(25, 118, 210, 0.3), rgba(156, 39, 176, 0.3));
  }
  100% {
    opacity: 0.2;
    transform: scale(0.98);
    filter: brightness(1) blur(0px);
    background: linear-gradient(45deg, rgba(25, 118, 210, 0.1), rgba(156, 39, 176, 0.1));
  }
`;

const EnhancedSkeleton = ({ 
  variant = "text", 
  width, 
  height, 
  sx = {}, 
  animationType = "enhanced-wave",
  ...props 
}) => {
  
  const getAnimationStyle = () => {
    switch (animationType) {
      case "enhanced-wave":
        return {
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
          '&::after': {
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            transform: 'translateX(-100%)',
            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent)',
            animation: `${enhancedWave} 2s infinite`,
            content: '""',
          }
        };
      
      case "pulse":
        return {
          animation: `${enhancedPulse} 1.8s ease-in-out infinite`,
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          borderRadius: '4px',
          transition: 'all 0.3s ease',
        };
      
      case "power-pulse":
        return {
          animation: `${powerPulse} 2.2s ease-in-out infinite`,
          borderRadius: '6px',
          transition: 'all 0.4s ease',
          position: 'relative',
          overflow: 'hidden',
        };
      
      case "classic-pulse":
        return {
          animation: `${pulse} 1.5s ease-in-out infinite`,
          backgroundColor: 'rgba(25, 118, 210, 0.1)',
        };
      
      case "gradient-flow":
        return {
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          backgroundSize: '200px 100%',
          animation: `${gradientFlow} 2s infinite`,
        };
      
      case "shimmer":
        return {
          background: 'linear-gradient(90deg, #f6f7f8 0px, #edeef1 40px, #f6f7f8 80px)',
          backgroundSize: '1000px 100%',
          animation: `${shimmer} 2.5s infinite`,
        };
      
      case "rainbow-wave":
        return {
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: 'rgba(0, 0, 0, 0.06)',
          '&::after': {
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            transform: 'translateX(-100%)',
            background: 'linear-gradient(90deg, transparent, rgba(25, 118, 210, 0.3), rgba(156, 39, 176, 0.3), rgba(233, 30, 99, 0.3), transparent)',
            animation: `${enhancedWave} 2.2s infinite`,
            content: '""',
          }
        };
      
      default:
        return {
          animation: `${enhancedWave} 2s infinite`,
        };
    }
  };

  return (
    <Skeleton
      variant={variant}
      width={width}
      height={height}
      animation={false} // MUI 기본 애니메이션 비활성화
      sx={{
        ...getAnimationStyle(),
        ...sx, // 기존 sx 스타일 유지
      }}
      {...props}
    />
  );
};

export default EnhancedSkeleton; 